// File: frontend/src/pages/settings/SchoolSettings.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/axios';

const SchoolSettings = ({ embedded = false }) => {
  const { school, reloadSchoolSettings } = useSchool();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  // Tab 1: General Settings Form State
  const [generalData, setGeneralData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    session_year: '2026',
    principal_name: ''
  });

  // Tab 2: Branding Form State
  const [brandingData, setBrandingData] = useState({
    primary_color: '#2563eb',
    secondary_color: '#0f172a'
  });
  const [logoFile, setLogoFile] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  // Tab 3: Document Settings Form State
  const [docData, setDocData] = useState({
    receipt_header: '',
    result_footer: ''
  });

  // Tab 4: License State
  const [licenseData, setLicenseData] = useState({
    license_key: '',
    license_expiry: '',
    is_licensed: true
  });
  const [licenseStatus, setLicenseStatus] = useState(null);

  useEffect(() => {
    if (school) {
      setGeneralData({
        name: school.name || '',
        address: school.address || '',
        phone: school.phone || '',
        email: school.email || '',
        website: school.website || '',
        session_year: school.session_year || '2026',
        principal_name: school.principal_name || ''
      });
      setBrandingData({
        primary_color: school.primary_color || '#2563eb',
        secondary_color: school.secondary_color || '#0f172a'
      });
      setDocData({
        receipt_header: school.receipt_header || '',
        result_footer: school.result_footer || ''
      });
      setLicenseData({
        license_key: school.license_key || '',
        license_expiry: school.license_expiry ? school.license_expiry.split('T')[0] : '',
        is_licensed: Boolean(school.is_licensed)
      });
    }
  }, [school]);

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  /**
   * Check license status from API
   */
  const fetchLicenseStatus = async () => {
    try {
      const response = await api.get('/api/v1/settings/license');
      if (response.data?.success) {
        setLicenseStatus(response.data.data);
      }
    } catch (err) {
      console.error('Error checking license status:', err);
    }
  };

  /**
   * Save General Settings
   */
  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setMsg(null);
      setError(null);

      const response = await api.put('/api/v1/settings', generalData);
      if (response.data?.success) {
        setMsg('General school settings saved successfully!');
        reloadSchoolSettings();
      } else {
        setError(response.data?.message || 'Failed to save settings.');
      }
    } catch (err) {
      console.error('Error saving general settings:', err);
      setError(err.response?.data?.message || err.message || 'Error saving settings.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Save Branding (Colors & Image Uploads)
   */
  const handleSaveBranding = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setMsg(null);
      setError(null);

      // Save Colors first
      await api.put('/api/v1/settings', brandingData);

      // Upload logo if selected
      if (logoFile) {
        const logoFd = new FormData();
        logoFd.append('logo', logoFile);
        await api.post('/api/v1/settings/logo', logoFd);
      }

      // Upload stamp if selected
      if (stampFile) {
        const stampFd = new FormData();
        stampFd.append('stamp', stampFile);
        await api.post('/api/v1/settings/stamp', stampFd);
      }

      // Upload signature if selected
      if (signatureFile) {
        const sigFd = new FormData();
        sigFd.append('signature', signatureFile);
        await api.post('/api/v1/settings/signature', sigFd);
      }

      setMsg('Branding settings and media assets updated successfully!');
      reloadSchoolSettings();
    } catch (err) {
      console.error('Error updating branding:', err);
      setError(err.response?.data?.message || err.message || 'Error updating branding.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Save Document Settings
   */
  const handleSaveDocuments = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setMsg(null);
      setError(null);

      const response = await api.put('/api/v1/settings', docData);
      if (response.data?.success) {
        setMsg('Document settings saved successfully!');
        reloadSchoolSettings();
      } else {
        setError(response.data?.message || 'Failed to save document settings.');
      }
    } catch (err) {
      console.error('Error saving document settings:', err);
      setError(err.response?.data?.message || err.message || 'Error saving document settings.');
    } finally {
      setSubmitting(false);
    }
  };

  const [downloadingBackup, setDownloadingBackup] = useState(false);

  const handleDownloadSelfBackup = async () => {
    try {
      setDownloadingBackup(true);
      setError(null);
      const schoolId = user?.school_id || 1;
      const res = await api.get(`/api/v1/settings/schools/${schoolId}/backup`);
      if (res.data?.success) {
        const jsonStr = JSON.stringify(res.data.data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `backup-${generalData.name ? generalData.name.replace(/\s+/g, '_') : 'school'}-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setMsg('Complete deep tenant backup snapshot downloaded successfully!');
      }
    } catch (err) {
      console.error('Error downloading backup:', err);
      setError(err.response?.data?.message || 'Failed to download tenant backup snapshot.');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const [restoringBackup, setRestoringBackup] = useState(false);

  const handleUploadRestoreBackup = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setRestoringBackup(true);
        setMsg(null);
        setError(null);

        const jsonContent = JSON.parse(evt.target.result);
        const schoolId = user?.school_id || 1;

        const res = await api.post(`/api/v1/settings/schools/${schoolId}/restore`, jsonContent);
        if (res.data?.success) {
          setMsg(res.data.message || 'School data snapshot restored successfully!');
          reloadSchoolSettings();
        }
      } catch (err) {
        console.error('Error restoring backup file:', err);
        setError(err.response?.data?.message || 'Failed to restore backup snapshot. Invalid JSON file format.');
      } finally {
        setRestoringBackup(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  /**
   * Update License (Super Admin only)
   */
  const handleSaveLicense = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setMsg(null);
      setError(null);

      const response = await api.put('/api/v1/settings/license', licenseData);
      if (response.data?.success) {
        setMsg('License key and expiry updated successfully!');
        fetchLicenseStatus();
        reloadSchoolSettings();
      } else {
        setError(response.data?.message || 'Failed to update license.');
      }
    } catch (err) {
      console.error('Error updating license:', err);
      setError(err.response?.data?.message || err.message || 'Error updating license.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={embedded ? 'space-y-6' : 'min-h-screen bg-slate-50 flex flex-col'}>
      {/* Top Navbar */}
      {!embedded && (
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/dashboard/admin" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
                ←
              </Link>
              <div>
                <h1 className="font-bold text-slate-800 text-lg leading-tight">School Settings & Profile</h1>
                <p className="text-xs text-slate-500">Configure school metadata, branding assets, document headers & licensing</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/settings/users"
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-3.5 py-2 rounded-xl text-xs transition shadow-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span>User Management</span>
              </Link>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={embedded ? 'space-y-6' : 'flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6'}>
        
        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
              activeTab === 'general' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            🏫 1. General Settings
          </button>
          <button
            onClick={() => setActiveTab('branding')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
              activeTab === 'branding' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            🎨 2. Branding & Assets
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
              activeTab === 'documents' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            📄 3. Document Headers & Footers
          </button>
          <button
            onClick={() => setActiveTab('license')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition whitespace-nowrap ${
              activeTab === 'license' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            🔑 4. License Management
          </button>
          <Link
            to="/settings/users"
            className="px-5 py-2.5 rounded-xl text-xs font-bold transition whitespace-nowrap text-indigo-700 bg-indigo-50 hover:bg-indigo-100 flex items-center gap-1.5"
          >
            👥 5. User Management & Roles
          </Link>
        </div>

        {/* Alerts */}
        {msg && (
          <div className="p-4 bg-emerald-50 border-l-4 border-emerald-500 text-emerald-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{msg}</span>
            <button onClick={() => setMsg(null)} className="font-bold text-emerald-600">✕</button>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Tenant Backup & Restore Self-Service Banner */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                <span>📥 Tenant Data Backup & Disaster Recovery</span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Export or import a complete, tenant-isolated JSON snapshot of your school's data records (Students, Staff, User Accounts, Timetable, Fees & Settings).
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleDownloadSelfBackup}
                disabled={downloadingBackup || restoringBackup}
                className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 hover:brightness-110 text-white font-bold text-xs rounded-xl shadow-sm transition whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
              >
                <span className="text-sm">📥</span>
                <span>{downloadingBackup ? 'Downloading...' : 'Export Backup'}</span>
              </button>

              <label className={`px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl shadow-sm transition whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${restoringBackup ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <span className="text-sm">📤</span>
                <span>{restoringBackup ? 'Restoring...' : 'Restore Backup File'}</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleUploadRestoreBackup}
                  disabled={restoringBackup || downloadingBackup}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* TAB 1: General Settings */}
        {activeTab === 'general' && (
          <form onSubmit={handleSaveGeneral} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h2 className="font-bold text-slate-900 text-base border-b pb-3">School Information</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">School Name *</label>
                <input
                  type="text"
                  value={generalData.name}
                  onChange={(e) => setGeneralData({ ...generalData, name: e.target.value })}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-primary focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Principal Name</label>
                <input
                  type="text"
                  value={generalData.principal_name}
                  onChange={(e) => setGeneralData({ ...generalData, principal_name: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Official Phone</label>
                <input
                  type="text"
                  value={generalData.phone}
                  onChange={(e) => setGeneralData({ ...generalData, phone: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Official Email</label>
                <input
                  type="email"
                  value={generalData.email}
                  onChange={(e) => setGeneralData({ ...generalData, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Website URL</label>
                <input
                  type="text"
                  value={generalData.website}
                  onChange={(e) => setGeneralData({ ...generalData, website: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Academic Session Year</label>
                <input
                  type="text"
                  value={generalData.session_year}
                  onChange={(e) => setGeneralData({ ...generalData, session_year: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">School Physical Address</label>
                <textarea
                  rows="2"
                  value={generalData.address}
                  onChange={(e) => setGeneralData({ ...generalData, address: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                ></textarea>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save General Settings'}
              </button>
            </div>
          </form>
        )}

        {/* TAB 2: Branding & Color Scheme */}
        {activeTab === 'branding' && (
          <form onSubmit={handleSaveBranding} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h2 className="font-bold text-slate-900 text-base border-b pb-3">Theme Colors & Asset Uploads</h2>

            {/* Colors Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Primary Brand Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandingData.primary_color}
                    onChange={(e) => setBrandingData({ ...brandingData, primary_color: e.target.value })}
                    className="w-12 h-10 p-1 rounded-lg border border-slate-300 cursor-pointer bg-white"
                  />
                  <span className="font-mono text-xs font-bold text-slate-800 uppercase">{brandingData.primary_color}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Secondary Accent Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={brandingData.secondary_color}
                    onChange={(e) => setBrandingData({ ...brandingData, secondary_color: e.target.value })}
                    className="w-12 h-10 p-1 rounded-lg border border-slate-300 cursor-pointer bg-white"
                  />
                  <span className="font-mono text-xs font-bold text-slate-800 uppercase">{brandingData.secondary_color}</span>
                </div>
              </div>
            </div>

            {/* Media Uploads Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Logo Upload */}
              <div className="p-4 border border-slate-200 rounded-xl space-y-3">
                <label className="block text-xs font-bold text-slate-800 uppercase">School Logo</label>
                {logoFile || school.logo ? (
                  <div className="w-20 h-20 mx-auto border rounded-xl overflow-hidden p-1 bg-slate-50 flex items-center justify-center">
                    <img
                      src={logoFile ? URL.createObjectURL(logoFile) : getImageUrl(school.logo)}
                      alt="Logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 mx-auto border border-dashed rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-xs font-bold">
                    No Logo
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files[0])}
                  className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                />
              </div>

              {/* Stamp Upload */}
              <div className="p-4 border border-slate-200 rounded-xl space-y-3">
                <label className="block text-xs font-bold text-slate-800 uppercase">Official School Stamp</label>
                {stampFile || school.stamp ? (
                  <div className="w-20 h-20 mx-auto border rounded-xl overflow-hidden p-1 bg-slate-50 flex items-center justify-center">
                    <img
                      src={stampFile ? URL.createObjectURL(stampFile) : getImageUrl(school.stamp)}
                      alt="Stamp"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 mx-auto border border-dashed rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-xs font-bold">
                    No Stamp
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setStampFile(e.target.files[0])}
                  className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                />
              </div>

              {/* Signature Upload */}
              <div className="p-4 border border-slate-200 rounded-xl space-y-3">
                <label className="block text-xs font-bold text-slate-800 uppercase">Principal Signature</label>
                {signatureFile || school.principal_signature ? (
                  <div className="w-20 h-20 mx-auto border rounded-xl overflow-hidden p-1 bg-slate-50 flex items-center justify-center">
                    <img
                      src={signatureFile ? URL.createObjectURL(signatureFile) : getImageUrl(school.principal_signature)}
                      alt="Signature"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 mx-auto border border-dashed rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 text-xs font-bold">
                    No Signature
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSignatureFile(e.target.files[0])}
                  className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                />
              </div>

            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50"
              >
                {submitting ? 'Uploading Assets...' : 'Save Branding & Media'}
              </button>
            </div>
          </form>
        )}

        {/* TAB 3: Document Headers & Footers */}
        {activeTab === 'documents' && (
          <form onSubmit={handleSaveDocuments} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
            <h2 className="font-bold text-slate-900 text-base border-b pb-3">Receipt & Report Card Customization</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Fee Receipt Header Title / Notice</label>
                <input
                  type="text"
                  placeholder="e.g. Official Fee Payment Receipt"
                  value={docData.receipt_header}
                  onChange={(e) => setDocData({ ...docData, receipt_header: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Result Card Footer Disclaimer</label>
                <textarea
                  rows="3"
                  placeholder="e.g. This report card is computer generated and officially valid."
                  value={docData.result_footer}
                  onChange={(e) => setDocData({ ...docData, result_footer: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                ></textarea>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save Document Templates'}
              </button>
            </div>
          </form>
        )}

        {/* TAB 4: License Management (Super Admin only) */}
        {activeTab === 'license' && (
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center justify-between border-b pb-3">
              <h2 className="font-bold text-slate-900 text-base">Software License Status & Expiry</h2>
              {licenseStatus && (
                <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${
                  licenseStatus.is_expired ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                }`}>
                  Status: {licenseStatus.status}
                </span>
              )}
            </div>

            {/* License Overview Badge */}
            {licenseStatus && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                <div>
                  <span className="text-slate-400 font-semibold uppercase block">License Key</span>
                  <span className="font-mono font-bold text-primary text-sm">{licenseStatus.license_key}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-semibold uppercase block">Expiration Date</span>
                  <span className="font-mono font-bold text-slate-900 text-sm">
                    {new Date(licenseStatus.license_expiry).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-semibold uppercase block">Days Remaining</span>
                  <span className="font-bold text-emerald-600 text-sm">{licenseStatus.days_remaining} Days</span>
                </div>
              </div>
            )}

            {/* License Key Edit Form (super_admin check) */}
            {user?.role === 'super_admin' ? (
              <form onSubmit={handleSaveLicense} className="space-y-4 pt-2">
                <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Update License Key Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">License Key *</label>
                    <input
                      type="text"
                      value={licenseData.license_key}
                      onChange={(e) => setLicenseData({ ...licenseData, license_key: e.target.value })}
                      required
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Expiry Date *</label>
                    <input
                      type="date"
                      value={licenseData.license_expiry}
                      onChange={(e) => setLicenseData({ ...licenseData, license_expiry: e.target.value })}
                      required
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                    />
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50"
                  >
                    {submitting ? 'Updating License...' : 'Update License Key'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-xs">
                🔒 Note: License key renewal can only be modified by Super Administrators.
              </div>
            )}
          </div>
        )}

      </main>
    </div>
  );
};

export default SchoolSettings;
